import type { ThemeId } from '../../../components/common/Theme/ThemeContext';

/**
 * URL builder for the Orchestration FO standalone SPA (embedded via iframe).
 *
 * JWT + tenant go in the URL hash (not the query) so they are never written to
 * IIS/CDN access logs or the Referer header. Theme goes in the query string.
 *
 * Produces: {base}{foPath}?theme=dark#access_token=<jwt>&tenant_id=<uuid>
 *
 * Configure the origin with REACT_APP_ORCHESTRATION_FO_URL
 * (dev: http://localhost:5173, prod: https://orcv2.selficai.com).
 */
const DEFAULT_FO_BASE_URL = 'http://localhost:5173';

/** Guards against the literal "undefined"/"null" strings localStorage can hold. */
function readStoredValue(key: string): string {
  const raw = localStorage.getItem(key)?.trim() ?? '';
  return raw === 'undefined' || raw === 'null' ? '' : raw;
}

export function getOrchestrationFoBaseUrl(): string {
  const configured = process.env.REACT_APP_ORCHESTRATION_FO_URL?.trim();
  return (configured || DEFAULT_FO_BASE_URL).replace(/\/+$/, '');
}

/** Origin of FO for postMessage validation (no path). */
export function getOrchestrationFoOrigin(): string {
  try {
    return new URL(getOrchestrationFoBaseUrl()).origin;
  } catch {
    return 'http://localhost:5173';
  }
}

/** Normalize splat / path to a FO pathname (always starts with /). */
export function normalizeFoPath(foPath?: string | null): string {
  const raw = (foPath ?? '').trim();
  if (!raw || raw === '/') return '/workflows';
  const withSlash = raw.startsWith('/') ? raw : `/${raw}`;
  // Strip accidental portal prefix if present
  const stripped = withSlash.replace(/^\/portal\/orchestration(?=\/|$)/, '');
  if (!stripped || stripped === '/') return '/workflows';
  return stripped.startsWith('/') ? stripped : `/${stripped}`;
}

export function buildOrchestrationFoSrc(
  baseUrl: string,
  theme: ThemeId,
  foPath: string = '/workflows',
): string {
  const base = (baseUrl || DEFAULT_FO_BASE_URL).replace(/\/+$/, '');
  const path = normalizeFoPath(foPath);
  const token = readStoredValue('token');
  const tenantId = readStoredValue('tenant_id');

  const url = new URL(`${base}${path}`);
  url.searchParams.set('theme', theme === 'black' ? 'dark' : 'light');

  const hash = new URLSearchParams();
  if (token) hash.set('access_token', token);
  if (tenantId) hash.set('tenant_id', tenantId);
  const hashStr = hash.toString();
  if (hashStr) url.hash = hashStr;

  return url.toString();
}
